import { memo } from 'react'
import { twMerge } from 'tailwind-merge'
import { ActionPopover, Button as UIButton } from '@highlight/ui'
import type { ChatMessage } from '../../../../types'
import { MessageActions } from './message-actions'

export interface MessageActionsRowProps {
  message: ChatMessage
  isMostRecent?: boolean
  containsAttachment: boolean
  /** A live turn suppresses the row on the most-recent message. */
  isTurnActive: boolean
  allowUserEditing?: boolean
  isEditing: boolean
  onStartEditing: () => void
  onSaveEdit: () => void
  onCancelEdit: () => void
  onRedoTurn: () => Promise<void>
}

export const MessageActionsRow = memo(function MessageActionsRow({
  message,
  isMostRecent,
  containsAttachment,
  isTurnActive,
  allowUserEditing = true,
  isEditing,
  onStartEditing,
  onSaveEdit,
  onCancelEdit,
  onRedoTurn,
}: MessageActionsRowProps) {
  const isAssistant = message.role === 'assistant'
  const show = !isMostRecent || !isTurnActive

  return (
    <div
      data-message-actions
      className={twMerge(
        'relative mt-1 flex items-center',
        message.role === 'user' && 'justify-end',
        !isAssistant && containsAttachment && 'mb-9',
      )}
    >
      {show && (
        <div className="flex items-center">
          {isEditing ? (
            <ActionPopover.Popup
              id="edit-actions"
              role="group"
              aria-label="Edit message actions"
              className="gap-1.5 border-none bg-transparent p-0 shadow-none"
            >
              <UIButton
                size="sm"
                shape="rounded"
                variant="secondary"
                onClick={onCancelEdit}
                aria-label="Cancel editing"
              >
                Cancel
              </UIButton>
              <UIButton
                size="sm"
                shape="rounded"
                variant="primary"
                tone="success"
                onClick={onSaveEdit}
                aria-label="Save changes"
              >
                Submit
              </UIButton>
            </ActionPopover.Popup>
          ) : (
            <MessageActions
              isMostRecent={isMostRecent ?? true}
              isTurnActive={isTurnActive}
              allowUserEditing={allowUserEditing}
              message={message}
              editMessage={onStartEditing}
              onRedoTurn={onRedoTurn}
            />
          )}
        </div>
      )}
    </div>
  )
})
